import { client } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  Tag,
  TagKind,
  CreateTagInput,
  UpdateTagInput,
  AssignLabelInput,
  AssignLabelResponse,
  Condition,
  EvaluatePreviewResponse,
  TriggerSweepResponse,
} from '@/types/tags';

export interface ListTagsParams {
  kind?: TagKind;
  search?: string;
}

export async function listTags(params?: ListTagsParams): Promise<Tag[]> {
  const { data } = await client.get<ApiResponse<Tag[]>>('/api/v1/tags', { params });
  return data.data ?? [];
}

export async function getTag(id: string): Promise<Tag> {
  const { data } = await client.get<ApiResponse<Tag>>(`/api/v1/tags/${id}`);
  return data.data;
}

export async function createTag(input: CreateTagInput): Promise<Tag> {
  const { data } = await client.post<ApiResponse<Tag>>('/api/v1/tags', input);
  return data.data;
}

export async function updateTag(id: string, input: UpdateTagInput): Promise<Tag> {
  const { data } = await client.put<ApiResponse<Tag>>(`/api/v1/tags/${id}`, input);
  return data.data;
}

export async function deleteTag(id: string): Promise<void> {
  await client.delete(`/api/v1/tags/${id}`);
}

export async function assignTag(
  id: string,
  input: AssignLabelInput,
): Promise<AssignLabelResponse> {
  const { data } = await client.post<ApiResponse<AssignLabelResponse>>(
    `/api/v1/tags/${id}/assign`,
    input,
  );
  return data.data;
}

export async function unassignTag(
  id: string,
  input: AssignLabelInput,
): Promise<AssignLabelResponse> {
  const { data } = await client.post<ApiResponse<AssignLabelResponse>>(
    `/api/v1/tags/${id}/unassign`,
    input,
  );
  return data.data;
}

export async function listTagUsers(id: string, limit = 50, offset = 0): Promise<string[]> {
  const { data } = await client.get<ApiResponse<string[]>>(`/api/v1/tags/${id}/users`, {
    params: { limit, offset },
  });
  return data.data ?? [];
}

export async function getUserTags(userId: string): Promise<Tag[]> {
  const { data } = await client.get<ApiResponse<Tag[]>>(`/api/v1/users/${userId}/tags`);
  return data.data ?? [];
}

export async function evaluatePreview(rule: Condition): Promise<EvaluatePreviewResponse> {
  const { data } = await client.post<ApiResponse<EvaluatePreviewResponse>>(
    '/api/v1/tags/evaluate-preview',
    { rule },
  );
  return data.data;
}

// Admin-only: enqueues a nightly-sweep job. Worker picks it up within ≤30s.
export async function triggerSweep(): Promise<TriggerSweepResponse> {
  const { data } = await client.post<ApiResponse<TriggerSweepResponse>>('/api/v1/tags/evaluate');
  return data.data;
}
